import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import {
  getPricingSchedule,
  getPricingScheduleWithFree,
  getOpsChatIntakeTiers,
  formatMonthlyUsd,
  TIER_LABOR_HOURS_SAVED_2026,
} from "../lib/pricing";
import { device, useBreakpoints } from "../lib/breakpoints";
import { APP_NAME } from "../helpers/constants";

const OPS_VS_SUITE_ROWS = [
  {
    category: "Messaging & Leo AI",
    features: [
      {
        name: "24/7 AI triage (magic link per tenant)",
        ops: true,
        suite: true,
      },
      {
        name: "Knowledge base & FAQs for Leo AI",
        ops: true,
        suite: true,
      },
      {
        name: "Emergency escalation",
        ops: "Email + SMS + voice",
        suite: "Email + SMS + voice on every tier",
      },
    ],
  },
  {
    category: `${APP_NAME} OS (every suite tier)`,
    features: [
      {
        name: "In-app rent ledgers & expense books",
        ops: false,
        suite: "Included on Free through Scale",
      },
      {
        name: "Rent collection & automated reminders",
        ops: false,
        suite: "Included on every tier",
      },
      {
        name: "Late fees, maintenance, leasing, analytics, taxes, sale-ready exports",
        ops: false,
        suite: "Included on every tier",
      },
      {
        name: "Advanced triage audit",
        ops: false,
        suite: "Included on every tier",
      },
    ],
  },
  {
    category: "Scope",
    features: [
      {
        name: "Messaging-first add-on (legacy parallel SKUs)",
        ops: true,
        suite: false,
      },
      {
        name: "Primary product: Starter, Growth, or Scale (door bands)",
        ops: false,
        suite: true,
      },
      {
        name: "Door limits",
        ops: "Match the suite tier you pick",
        suite: "3 / 15 / 50 / 150 doors (Free / Starter / Growth / Scale) - same full OS",
      },
    ],
  },
];

/** Market 2026 ladder: Free, Starter, Growth, Scale only (no Enterprise column). */
const UNIFIED_2026_FEATURES = [
  {
    category: "ROI & labor savings",
    features: [
      {
        name: "Est. labor hours saved (per month)",
        free: TIER_LABOR_HOURS_SAVED_2026.free,
        starter: TIER_LABOR_HOURS_SAVED_2026.starter,
        growth: TIER_LABOR_HOURS_SAVED_2026.growth,
        scale: TIER_LABOR_HOURS_SAVED_2026.scale,
      },
    ],
  },
  {
    category: "OpsChat & Leo AI (every tier)",
    features: [
      {
        name: "24/7 AI triage (magic link per tenant)",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Knowledge base & FAQs for Leo AI",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Leo AI Assistant",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
    ],
  },
  {
    category: "Alerts & triage",
    features: [
      {
        name: "Emergency escalation (email on urgent issues)",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "SMS & voice triage (verified emergencies)",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Advanced audit trail",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
    ],
  },
  {
    category: "Full LandlordOps OS (every tier)",
    features: [
      {
        name: "Automated Rent Collection",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Late fee automation",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Vendor Management",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "AI vendor bidding",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Ledger Syncing",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Leasing funnel: prospect chat & tour booking",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Deal Analyzer: DSCR, NOI, investor analytics",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Schedule E Tax Reporting",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Sale-ready rent roll & lease export",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
      {
        name: "Portfolio dashboard & reporting",
        free: true,
        starter: true,
        growth: true,
        scale: true,
      },
    ],
  },
];

const LEGACY_2025_FEATURES = [
  {
    category: "All Plans Include",
    features: [
      {
        name: "Leo AI: 24/7 AI triage",
        starter: true,
        pro: true,
        business: true,
        growth: true,
      },
      {
        name: "Rent collection & automated reminders",
        starter: true,
        pro: true,
        business: true,
        growth: true,
      },
      {
        name: "Late-fee rules & tracking",
        starter: true,
        pro: true,
        business: true,
        growth: true,
      },
      {
        name: "Maintenance triage & vendor routing",
        starter: true,
        pro: true,
        business: true,
        growth: true,
      },
      {
        name: "Leasing funnel: prospect chat & tour booking",
        starter: true,
        pro: true,
        business: true,
        growth: true,
      },
      {
        name: "Deal Analyzer: DSCR, NOI, investor analytics",
        starter: true,
        pro: true,
        business: true,
        growth: true,
      },
      {
        name: "Schedule E & tax-ready exports",
        starter: true,
        pro: true,
        business: true,
        growth: true,
      },
      {
        name: "Portfolio dashboard & reporting",
        starter: true,
        pro: true,
        business: true,
        growth: true,
      },
    ],
  },
];

const FeatureMatrix = ({ legacyPricing = false, opsChatCompare = false }) => {
  const { isMobile } = useBreakpoints();
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [opsCompareTab, setOpsCompareTab] = useState("opschat");

  const schedule = useMemo(
    () =>
      legacyPricing
        ? getPricingSchedule("legacy_2025")
        : getPricingScheduleWithFree("market_2026"),
    [legacyPricing]
  );
  const tierKeysOrdered = useMemo(
    () =>
      legacyPricing
        ? ["starter", "pro", "growth", "scale"]
        : ["free", "starter", "growth", "scale"],
    [legacyPricing]
  );
  const tierList = useMemo(
    () => tierKeysOrdered.map((key) => schedule[key]).filter(Boolean),
    [schedule, tierKeysOrdered]
  );
  const matrixGridCols = useMemo(
    () => `2fr repeat(${tierList.length}, 1fr)`,
    [tierList.length]
  );
  const featureCategories = useMemo(
    () => (legacyPricing ? LEGACY_2025_FEATURES : UNIFIED_2026_FEATURES),
    [legacyPricing]
  );

  useEffect(() => {
    const valid = legacyPricing
      ? ["starter", "pro", "growth", "scale"]
      : ["free", "starter", "growth", "scale"];
    if (!valid.includes(selectedPlan)) setSelectedPlan(valid[0]);
  }, [legacyPricing, selectedPlan]);

  const opsChatPriceLabel = useMemo(() => {
    const entry = getOpsChatIntakeTiers(
      legacyPricing ? "legacy_2025" : "market_2026"
    )[0]?.monthlyUSD;
    return formatMonthlyUsd(
      Number.isFinite(Number(entry)) ? Number(entry) : 19.99
    );
  }, [legacyPricing]);

  const renderFeatureValue = (value) => {
    if (value === true) return <CheckIcon>✓</CheckIcon>;
    if (value === false) return <XIcon>✗</XIcon>;
    return <TextValue>{value}</TextValue>;
  };

  /** Free tier is $0; show "FREE" in the matrix instead of "$0/mo". */
  const formatMatrixTierPriceLine = (tier) => {
    if (!tier || typeof tier.monthlyUSD !== "number") return "";
    if (tier.monthlyUSD === 0) return "FREE";
    return `$${tier.monthlyUSD}/mo`;
  };

  const suiteFromMonthly = schedule.starter.monthlyUSD;

  if (opsChatCompare && isMobile) {
    const colKey = opsCompareTab === "opschat" ? "ops" : "suite";
    return (
      <MobileContainer>
        <OpsMatrixTitle>OpsChat vs full {APP_NAME}</OpsMatrixTitle>
        <MatrixSubtitle>
          Every suite tier (Starter, Growth, Scale) includes the same full
          LandlordOps OS - door caps are the only difference.
        </MatrixSubtitle>

        <PlanSelector>
          <PlanTab
            $active={opsCompareTab === "opschat"}
            onClick={() => setOpsCompareTab("opschat")}
          >
            <PlanTabName>OpsChat</PlanTabName>
            <PlanTabPrice>${opsChatPriceLabel}/mo</PlanTabPrice>
          </PlanTab>
          <PlanTab
            $active={opsCompareTab === "suite"}
            onClick={() => setOpsCompareTab("suite")}
          >
            <PlanTabName>Suite</PlanTabName>
            <PlanTabPrice>From ${suiteFromMonthly}/mo</PlanTabPrice>
          </PlanTab>
        </PlanSelector>

        <SelectedPlanCard>
          <PlanHeader>
            <PlanName>
              {opsCompareTab === "opschat" ? "OpsChat" : "LandlordOps suite"}
            </PlanName>
            <PlanPrice>
              <PlanPriceAmount>
                {opsCompareTab === "opschat"
                  ? `$${opsChatPriceLabel}`
                  : `$${suiteFromMonthly}`}
              </PlanPriceAmount>
              <PlanPricePeriod>/month</PlanPricePeriod>
            </PlanPrice>
            <PlanUnits>
              {opsCompareTab === "opschat"
                ? "Messaging & 24/7 AI triage only"
                : "Door limits by tier. Full Rental Command Center on every plan"}
            </PlanUnits>
            <PlanTagline>
              {opsCompareTab === "opschat"
                ? "Legacy messaging SKU"
                : "Same full OS - upgrade only for more doors"}
            </PlanTagline>
          </PlanHeader>

          <FeaturesList>
            {OPS_VS_SUITE_ROWS.map((category) => (
              <CategorySection key={category.category}>
                <CategoryTitle>{category.category}</CategoryTitle>
                {category.features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <FeatureLabel>{feature.name}</FeatureLabel>
                    <FeatureValue>
                      {renderFeatureValue(feature[colKey])}
                    </FeatureValue>
                  </FeatureItem>
                ))}
              </CategorySection>
            ))}
          </FeaturesList>
        </SelectedPlanCard>
      </MobileContainer>
    );
  }

  if (opsChatCompare) {
    return (
      <OpsCompareWrap>
        <OpsMatrixTitle>OpsChat vs full {APP_NAME}</OpsMatrixTitle>
        <MatrixSubtitle>
          Every suite tier (Starter, Growth, Scale) includes the same full
          LandlordOps OS - door caps are the only difference.
        </MatrixSubtitle>

        <OpsCompareTable>
          <OpsCompareHeader>
            <OpsFeatureCol>
              <MatrixCornerTitle>Features</MatrixCornerTitle>
              <MatrixCornerLead>Compare columns</MatrixCornerLead>
              <MatrixCornerMeta>
                Every suite column includes the full OS (rent, ledgers, vendors,
                Schedule E, Leo). Plans differ only by door count.
              </MatrixCornerMeta>
            </OpsFeatureCol>
            <OpsTierCol>
              <OpsTierName>OpsChat</OpsTierName>
              <OpsTierPrice>${opsChatPriceLabel}/mo</OpsTierPrice>
              <OpsTierUnits>Legacy messaging SKU</OpsTierUnits>
              <OpsTierTagline>Historical add-on</OpsTierTagline>
            </OpsTierCol>
            <OpsTierCol>
              <OpsTierName>Suite</OpsTierName>
              <OpsTierPrice>From ${suiteFromMonthly}/mo</OpsTierPrice>
              <OpsTierUnits>Starter, Growth, or Scale</OpsTierUnits>
              <OpsTierTagline>Full OS · door caps differ</OpsTierTagline>
            </OpsTierCol>
          </OpsCompareHeader>

          <OpsCompareBody>
            {OPS_VS_SUITE_ROWS.map((category) => (
              <React.Fragment key={category.category}>
                <OpsCategoryRow>
                  <OpsCategoryName>{category.category}</OpsCategoryName>
                  <OpsCategoryCell />
                  <OpsCategoryCell />
                </OpsCategoryRow>
                {category.features.map((feature, index) => (
                  <OpsFeatureDataRow key={`${category.category}-${index}`}>
                    <OpsFeatureName>{feature.name}</OpsFeatureName>
                    <OpsFeatureCell>
                      {renderFeatureValue(feature.ops)}
                    </OpsFeatureCell>
                    <OpsFeatureCell>
                      {renderFeatureValue(feature.suite)}
                    </OpsFeatureCell>
                  </OpsFeatureDataRow>
                ))}
              </React.Fragment>
            ))}
          </OpsCompareBody>
        </OpsCompareTable>
      </OpsCompareWrap>
    );
  }

  // Mobile view: Show one plan at a time with plan selector
  if (isMobile) {
    return (
      <MobileContainer>
        <MatrixTitle>Feature Comparison</MatrixTitle>
        <MatrixSubtitle>See exactly what each plan includes</MatrixSubtitle>

        <PlanSelector>
          {tierKeysOrdered.map((key) => {
            const tier = schedule[key];
            if (!tier) return null;
            return (
              <PlanTab
                key={key}
                $active={selectedPlan === key}
                onClick={() => setSelectedPlan(key)}
              >
                <PlanTabName>{tier.name}</PlanTabName>
                <PlanTabPrice>{formatMatrixTierPriceLine(tier)}</PlanTabPrice>
              </PlanTab>
            );
          })}
        </PlanSelector>

        <SelectedPlanCard>
          <PlanHeader>
            <PlanName>{schedule[selectedPlan]?.name}</PlanName>
            <PlanPrice>
              {schedule[selectedPlan]?.monthlyUSD === 0 ? (
                <PlanPriceAmount>FREE</PlanPriceAmount>
              ) : (
                <>
                  <PlanPriceAmount>
                    ${schedule[selectedPlan]?.monthlyUSD}
                  </PlanPriceAmount>
                  <PlanPricePeriod>/month</PlanPricePeriod>
                </>
              )}
            </PlanPrice>
            <PlanUnits>Up to {schedule[selectedPlan]?.unitCap} doors</PlanUnits>
            <PlanTagline>{schedule[selectedPlan]?.tagline}</PlanTagline>
          </PlanHeader>

          <FeaturesList>
            {featureCategories.map((category) => (
              <CategorySection key={category.category}>
                <CategoryTitle>{category.category}</CategoryTitle>
                {category.features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <FeatureLabel>{feature.name}</FeatureLabel>
                    <FeatureValue>
                      {renderFeatureValue(feature[selectedPlan])}
                    </FeatureValue>
                  </FeatureItem>
                ))}
              </CategorySection>
            ))}
          </FeaturesList>
        </SelectedPlanCard>
      </MobileContainer>
    );
  }

  // Desktop view: Show full table
  return (
    <Container>
      <MatrixTitle>Feature Comparison</MatrixTitle>
      <MatrixSubtitle>See exactly what each plan includes</MatrixSubtitle>

      <MatrixTable>
        <TableHeader $gridCols={matrixGridCols}>
          <FeatureColumn>
            <MatrixCornerTitle>Features</MatrixCornerTitle>
            <MatrixCornerLead>Compare plans</MatrixCornerLead>
            <MatrixCornerMeta>
              Each column header shows that plan&apos;s price and door{"\u00A0"}
              limit. Every tier includes the full {APP_NAME} OS: rent, vendors,
              ledgers, Schedule E, and Leo.
            </MatrixCornerMeta>
          </FeatureColumn>
          {tierList.map((tier) => (
            <TierColumn key={tier.name}>
              <TierName>{tier.name}</TierName>
              <TierPrice>{formatMatrixTierPriceLine(tier)}</TierPrice>
              <TierUnits>Up to {tier.unitCap} doors</TierUnits>
              <TierTagline>{tier.tagline}</TierTagline>
            </TierColumn>
          ))}
        </TableHeader>

        <TableBody>
          {featureCategories.map((category) => (
            <React.Fragment key={category.category}>
              <CategoryRow $gridCols={matrixGridCols}>
                <CategoryName>{category.category}</CategoryName>
                {tierKeysOrdered.map((key) => (
                  <CategoryCell key={key} />
                ))}
              </CategoryRow>

              {category.features.map((feature, index) => (
                <FeatureRow key={index} $gridCols={matrixGridCols}>
                  <FeatureName>{feature.name}</FeatureName>
                  {tierKeysOrdered.map((key) => (
                    <FeatureCell key={key}>
                      {renderFeatureValue(feature[key])}
                    </FeatureCell>
                  ))}
                </FeatureRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </MatrixTable>
    </Container>
  );
};

export default FeatureMatrix;

// Styled Components
const Container = styled.div`
  margin: 4rem 0;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const OpsCompareWrap = styled.div`
  margin: 4rem 0;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
`;

const OpsCompareTable = styled.div`
  width: 100%;
  min-width: 560px;
`;

const OpsCompareHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  background: var(--color-bg-secondary);
  border-bottom: 2px solid var(--color-border-light);
`;

const OpsFeatureCol = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const OpsTierCol = styled.div`
  padding: 1.5rem;
  text-align: center;
  border-left: 1px solid var(--color-border-light);
`;

const OpsTierName = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 0.5rem;
  font-family: var(--font-primary);
`;

const OpsTierPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.25rem;
  font-family: var(--font-primary);
`;

const OpsTierUnits = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin-bottom: 0.35rem;
`;

const OpsTierTagline = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  font-style: italic;
  line-height: 1.3;
`;

const OpsCompareBody = styled.div`
  display: contents;
`;

const OpsCategoryRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  background: #f8fafc;
  border-bottom: 1px solid var(--color-border-light);
`;

const OpsCategoryName = styled.div`
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  font-family: var(--font-secondary);
  background: #f1f5f9;
`;

const OpsCategoryCell = styled.div`
  padding: 1rem 1.5rem;
  border-left: 1px solid var(--color-border-light);
  background: #f1f5f9;
`;

const OpsFeatureDataRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  border-bottom: 1px solid var(--color-border-light);
  &:hover {
    background: #f8fafc;
  }
`;

const OpsFeatureName = styled.div`
  padding: 1rem 1.5rem;
  font-size: 0.95rem;
  color: var(--color-text);
  font-family: var(--font-secondary);
  display: flex;
  align-items: center;
`;

const OpsFeatureCell = styled.div`
  padding: 1rem 1.5rem;
  border-left: 1px solid var(--color-border-light);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MatrixTitle = styled.h3`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text);
  text-align: center;
  margin: 3rem 0 1rem 0;
  font-family: var(--font-primary);
`;

const OpsMatrixTitle = styled(MatrixTitle)`
  margin-top: 1.25rem;
`;

const MatrixSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-light);
  text-align: center;
  margin-bottom: 1.25rem;
  font-family: var(--font-secondary);
`;

const MatrixTable = styled.div`
  width: 100%;
  border-collapse: collapse;

  @media ${device.mobile} {
    overflow-x: auto;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$gridCols || "2fr repeat(4, 1fr)"};
  background: var(--color-bg-secondary);
  border-bottom: 2px solid var(--color-border-light);

  @media ${device.mobile} {
    min-width: 640px;
  }
`;

const MatrixCornerTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 0.4rem;
  font-family: var(--font-primary);
  letter-spacing: -0.02em;
`;

const MatrixCornerLead = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 0.45rem;
  font-family: var(--font-primary);
  line-height: 1.15;
`;

const MatrixCornerMeta = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin: 0;
  line-height: 1.5;
`;

const FeatureColumn = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const TierColumn = styled.div`
  padding: 1.5rem;
  text-align: center;
  border-left: 1px solid var(--color-border-light);
`;

const TierName = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 0.5rem;
  font-family: var(--font-primary);
`;

const TierPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.25rem;
  font-family: var(--font-primary);
`;

const TierUnits = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin-bottom: 0.5rem;
`;

const TierTagline = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  font-style: italic;
  line-height: 1.3;
`;

const TableBody = styled.div`
  display: contents;
`;

const CategoryRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$gridCols || "2fr repeat(4, 1fr)"};
  background: #f8fafc;
  border-bottom: 1px solid var(--color-border-light);

  @media ${device.mobile} {
    min-width: 640px;
  }
`;

const CategoryName = styled.div`
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  font-family: var(--font-secondary);
  background: #f1f5f9;
`;

const CategoryCell = styled.div`
  padding: 1rem 1.5rem;
  border-left: 1px solid var(--color-border-light);
  background: #f1f5f9;
`;

const FeatureRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$gridCols || "2fr repeat(4, 1fr)"};
  border-bottom: 1px solid var(--color-border-light);

  &:hover {
    background: #f8fafc;
  }

  @media ${device.mobile} {
    min-width: 640px;
  }
`;

const FeatureName = styled.div`
  padding: 1rem 1.5rem;
  font-size: 0.95rem;
  color: var(--color-text);
  font-family: var(--font-secondary);
  display: flex;
  align-items: center;
`;

const FeatureCell = styled.div`
  padding: 1rem 1.5rem;
  border-left: 1px solid var(--color-border-light);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckIcon = styled.span`
  color: var(--color-success);
  font-size: 1.2rem;
  font-weight: bold;
`;

const XIcon = styled.span`
  color: #ef4444;
  font-size: 1.2rem;
  font-weight: bold;
`;

const TextValue = styled.span`
  font-size: 0.9rem;
  color: var(--color-text);
  font-weight: 500;
  font-family: var(--font-secondary);
`;

// Mobile specific styles
const MobileContainer = styled.div`
  margin: 4rem 0;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const PlanSelector = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const PlanTab = styled.button`
  background: ${(props) =>
    props.$active ? "var(--color-primary)" : "var(--color-bg-secondary)"};
  color: ${(props) => (props.$active ? "white" : "var(--color-text)")};
  border: 1px solid var(--color-border-light);
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: var(--color-primary);
    color: white;
  }

  /* Ensure all child elements inherit the text color */
  * {
    color: inherit;
  }
`;

const PlanTabName = styled.span`
  margin-right: 0.5rem;
  color: inherit;
`;

const PlanTabPrice = styled.span`
  font-size: 0.9rem;
  color: inherit;
  opacity: 0.9;
`;

const SelectedPlanCard = styled.div`
  background: var(--color-bg-light);
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const PlanName = styled.h4`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.5rem;
  font-family: var(--font-primary);
`;

const PlanPrice = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 0.15rem 0.35rem;
  margin-bottom: 0.25rem;
  max-width: 100%;
  min-width: 0;
`;

const PlanPriceAmount = styled.span`
  font-size: clamp(1.45rem, min(10vw, 11vmin), 2.5rem);
  font-weight: 700;
  color: var(--color-primary);
  font-family: var(--font-primary);
  letter-spacing: -0.03em;
  line-height: 1.05;
  min-width: 0;
`;

const PlanPricePeriod = styled.span`
  font-size: clamp(0.78rem, min(2.8vw, 3.5vmin), 1.1rem);
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  flex-shrink: 0;
`;

const PlanUnits = styled.div`
  font-size: 1rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  margin-bottom: 0.5rem;
`;

const PlanTagline = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-muted);
  font-family: var(--font-secondary);
  font-style: italic;
  line-height: 1.3;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const CategorySection = styled.div`
  border-bottom: 1px solid var(--color-border-light);
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const CategoryTitle = styled.h5`
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 1rem;
  font-family: var(--font-secondary);
  text-align: left;
`;

const FeatureItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--color-border-light);

  &:last-child {
    border-bottom: none;
  }
`;

const FeatureLabel = styled.span`
  font-size: 0.95rem;
  color: var(--color-text);
  font-family: var(--font-secondary);
  flex-grow: 1;
  text-align: left;
`;

const FeatureValue = styled.span`
  font-size: 0.95rem;
  color: var(--color-text);
  font-weight: 500;
  font-family: var(--font-secondary);
  text-align: right;
`;
